import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackAssessmentCTA } from '../utils/analytics';
import { useLibraryStats } from '../hooks/useLibraryStats';
import { TrustStackFooterBlock } from './TrustStack';

const BASE_CLS = 'text-xs transition-colors text-left';
const INACTIVE_CLS = `${BASE_CLS} text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300`;
const ACTIVE_CLS = `${BASE_CLS} text-indigo-600 dark:text-indigo-400 font-semibold`;
const HEADING_CLS = 'text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 text-left';

export default function SEOFooter({ brandName = 'CPDForge AI', logoSrc = '/cpdforge-icon.webp' }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { stats: libStats } = useLibraryStats();

  const L = (path, label, testId, options = {}) => {
    const isActive = pathname === path;
    return (
      <a
        key={path}
        href={path}
        onClick={(e) => {
          e.preventDefault();
          if (options.onClick) options.onClick(e);
          navigate(path);
        }}
        className={isActive ? ACTIVE_CLS : INACTIVE_CLS}
        data-testid={testId}
        aria-current={isActive ? 'page' : undefined}
      >
        {label}
      </a>
    );
  };

  return (
    <footer className="border-t border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-950" data-testid="seo-footer">
      <div className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-6">
          {/* Column 1 — Platform */}
          <div>
            <p className={HEADING_CLS}>Platform</p>
            <div className="flex flex-col gap-2.5 items-start">
              {L('/compliance-training-software', 'Compliance Training Software', 'footer-platform-cts')}
              {L('/ai-course-creation-compliance', 'AI Course Creation', 'footer-platform-aicc')}
              {L('/regulatory-change-tracking-software', 'Regulatory Change Tracking', 'footer-platform-rct')}
              {L('/ai-training-platform', 'AI Training Platform', 'footer-platform-atp')}
              {L('/automated-compliance-training', 'Automated Training', 'footer-platform-act')}
              {L('/corporate-compliance-training-ai', 'Corporate Training AI', 'footer-platform-cctai')}
            </div>
          </div>

          {/* Column 2 — Guides */}
          <div>
            <p className={HEADING_CLS}>Guides</p>
            <div className="flex flex-col gap-2.5 items-start">
              {L('/why-compliance-training-fails', 'Why Training Fails', 'footer-guides-fails')}
              {L('/how-to-improve-compliance-training', 'How to Improve Training', 'footer-guides-improve')}
              {L('/compliance-training-effectiveness', 'Training Effectiveness', 'footer-guides-effectiveness')}
              {L('/keep-compliance-training-up-to-date', 'Keeping Training Current', 'footer-guides-current')}
              {L('/guides/microlearning-training-guide', 'Microlearning Training Guide', 'footer-guides-microlearning')}
              {L('/help/mfa-guide', 'MFA Setup Guide', 'footer-guides-mfa')}
            </div>
          </div>

          {/* Column 3 — Compare Providers */}
          <div>
            <p className={HEADING_CLS}>Compare Providers</p>
            <div className="flex flex-col gap-2.5 items-start">
              {L('/best-compliance-training-platforms', 'Best Compliance Training Platforms', 'footer-compare-hub')}
              {L('/compare/health-safety-training-providers', 'Health & Safety Training Providers', 'footer-compare-hs')}
              {L('/compare/corporate-compliance-training-platforms', 'Compliance & GRC Software', 'footer-compare-compliance')}
              {L('/compare/learning-management-systems', 'LMS & HR Training Platforms', 'footer-compare-lms')}
              {L('/compare/esg-sustainability-platforms', 'ESG & Sustainability Platforms', 'footer-compare-esg')}
              {L('/compare/general-training-platforms', 'General Training Platforms', 'footer-compare-general')}
            </div>
          </div>

          {/* Column 4 — Product */}
          <div>
            <p className={HEADING_CLS}>Product</p>
            <div className="flex flex-col gap-2.5 items-start">
              {L('/features', 'Features', 'footer-product-features')}
              {L('/library', 'Standard Library', 'footer-product-library')}
              {L('/library/programmes', 'Specialist Programmes', 'footer-product-programmes')}
              {L('/credentials', 'Credential Pathways', 'footer-product-credentials')}
              {L('/ai', 'AI Governance & Assurance', 'footer-product-ai-governance')}
              {L('/about', 'About', 'footer-product-about')}
              {L('/pricing', 'Pricing', 'footer-product-pricing')}
              {L('/demo', 'Demo', 'footer-product-demo')}
              {L('/compliance-training-assessment', 'Find Your Platform', 'footer-product-assessment', {
                onClick: () => {
                  trackAssessmentCTA({
                    text: 'Find Your Platform',
                    type: 'text_link',
                    destination: '/compliance-training-assessment',
                    position: 'footer',
                  });
                  sessionStorage.setItem('assessment_entry_source', window.location.pathname);
                  sessionStorage.setItem('assessment_cta_location', 'footer');
                }
              })}
            </div>
          </div>

          {/* Column 5 — Trust & Accreditation */}
          <div>
            <p className={HEADING_CLS}>Trust & Accreditation</p>
            <div className="flex flex-col gap-2.5 items-start">
              {L('/accreditation', 'Core Compliant Accreditation', 'footer-trust-accreditation')}
              {L('/accreditation/registry', 'Accreditation Registry', 'footer-trust-registry')}
              {L('/verification', 'How Verification Works', 'footer-trust-verification')}
              {L('/professional-standards-institute', 'Professional Standards Institute', 'footer-trust-cpfpsi')}
              {L('/verify/sample', 'Verify a Credential', 'footer-trust-verify')}
              {L('/trust', 'Trust Centre', 'footer-trust-centre')}
              {L('/knowledge-hub', 'Knowledge Hub', 'footer-resources-kh')}
              {L('/support', 'Support', 'footer-resources-support')}
              {L('/money-back-guarantee', 'Money-Back Guarantee', 'footer-resources-guarantee')}
              {L('/privacy', 'Privacy Policy', 'footer-resources-privacy')}
              {L('/terms', 'Terms of Service', 'footer-resources-terms')}
            </div>
          </div>
        </div>

        {/* Trust stack block — names all three layers with distinct roles */}
        <div className="mt-10">
          <TrustStackFooterBlock />
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-slate-100 dark:border-slate-800/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src={logoSrc} alt={brandName} className="w-5 h-5 rounded-md object-contain" />
            <span className="text-xs text-slate-400 dark:text-slate-600">&copy; {new Date().getFullYear()} {brandName}. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-4">
            {libStats && (
              <span className="text-[10px] text-slate-400 dark:text-slate-600" data-testid="footer-library-stats">
                {libStats.courses} courses &middot; {libStats.lessons} lessons &middot; {libStats.learning_hours}+ hours
              </span>
            )}
            <span className="text-xs text-slate-400 dark:text-slate-600">
              {L('/professional-standards-institute', 'Professional Standards Institute', 'footer-entity-anchor')}
            </span>
          </div>
        </div>

        {/* Legal entity */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/40">
          <p className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-600" data-testid="footer-legal-entity">
            CPDForge is operated by CPDFORGE AI LTD, registered in England and Wales under company number 17306615. Registered office: 66 Paul Street, London, England, United Kingdom, EC2A 4NA.
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-400 dark:text-slate-600" data-testid="footer-ico-registration">
            ICO Registration number: ZC189124{' '}
            <a
              href="https://ico.org.uk/ESDWebPages/Entry/ZC189124"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="underline hover:text-slate-700 dark:hover:text-slate-300"
              data-testid="footer-ico-register-link"
            >
              (verify on the ICO public register)
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
